import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import { makeStyles, alpha } from '@material-ui/core/styles';
import { Content, Header, Page } from '@backstage/core-components';
import { useApi, fetchApiRef, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { formatInstantInUserTimezone } from '../../utils/formatInstantInUserTimezone';
import { useAdminGroupMember } from '../devtools/AdminGroupMemberContext';
import { AdminAllTicketsPanel } from './AdminAllTicketsPanel';
import type { RequestDoc } from './requestDoc';
import { sortTimelineEvents } from './RequestDetailPage';

export type { RequestDoc } from './requestDoc';
import {
  effectiveRequestStatus,
  requestDocIndicatesRejected,
  requestDocIndicatesUserCancelled,
} from '../../utils/requestRejection';

const useRequestRowStyles = makeStyles(theme => ({
  clickableRow: {
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
}));

/** Extract Firebase Console index-creation URL from Firestore "requires an index" error. */
function getFirestoreIndexUrl(errorMessage: string): string | null {
  const match = errorMessage.match(
    /https:\/\/console\.firebase\.google\.com\/[^\s"']+create_composite=[A-Za-z0-9_-]+/,
  );
  return match ? match[0] : null;
}

function parseAuditTimestamp(iso: string | undefined): number {
  if (!iso?.trim()) return 0;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

type RequestsSortBy = 'created' | 'last_updated';

/** Compact metering: blue dot = event with timestamp, grey dot = event without. Uses sorted order. */
function UpdatesMetering({ events }: { events: Array<{ event: string; timestamp?: string }> }) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const shadowColor =
    theme.palette.type === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400];
  const sorted = sortTimelineEvents(events);
  if (!sorted.length) return <Typography variant="caption" color="textSecondary">—</Typography>;
  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 4 }}>
      {sorted.map((e, i) => {
        const withTs = Boolean(e.timestamp?.trim());
        return (
          <Box
            key={`${e.event}-${i}`}
            component="span"
            title={
              withTs
                ? `${e.event.replace(/_/g, ' ').replace(/\bbackstage\b/gi, 'Iron Phoenix')}: ${formatInstantInUserTimezone(e.timestamp)}`
                : e.event.replace(/_/g, ' ').replace(/\bbackstage\b/gi, 'Iron Phoenix')
            }
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: withTs ? primaryColor : shadowColor,
            }}
          />
        );
      })}
    </Box>
  );
}

function RequestsTable({
  requests,
  loading,
  error,
  emptyMessage,
  adminMember,
}: {
  requests: RequestDoc[];
  loading: boolean;
  error: string | null;
  emptyMessage: string;
  /** When true, show Firestore index-creation help + Firebase Console link (admin-only). */
  adminMember: boolean | null;
}) {
  const rowStyles = useRequestRowStyles();
  const theme = useTheme();
  const [ticketSearch, setTicketSearch] = useState('');
  const [sortBy, setSortBy] = useState<RequestsSortBy>('last_updated');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const searchTrim = ticketSearch.trim();
  const displayedRequests = useMemo(() => {
    let list = [...requests];
    if (searchTrim) {
      list = list.filter(r => String(r.request_id).includes(searchTrim));
    }
    list.sort((a, b) => {
      const aT =
        sortBy === 'created'
          ? parseAuditTimestamp(a.audit?.created_at)
          : parseAuditTimestamp(a.audit?.last_updated_at);
      const bT =
        sortBy === 'created'
          ? parseAuditTimestamp(b.audit?.created_at)
          : parseAuditTimestamp(b.audit?.last_updated_at);
      const cmp = aT - bT;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [requests, searchTrim, sortBy, sortDir]);

  const handleSort = (property: RequestsSortBy) => {
    const isActive = sortBy === property;
    const isAsc = isActive && sortDir === 'asc';
    setSortDir(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    const indexUrl = getFirestoreIndexUrl(error);
    let errorBody: ReactNode;
    if (indexUrl && adminMember === null) {
      errorBody = (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
          <CircularProgress size={28} />
        </Box>
      );
    } else if (indexUrl && adminMember) {
      errorBody = (
        <>
          <Typography variant="body1" style={{ marginBottom: 8 }}>
            Firestore needs a <strong>composite index</strong> for this query. Create it once:
          </Typography>
          <ol style={{ margin: '8px 0', paddingLeft: 24 }}>
            <li>Click the button below to open Firebase Console.</li>
            <li>Click <strong>Create index</strong> and wait until it finishes building (usually a few minutes).</li>
            <li>Refresh this page.</li>
          </ol>
          <Button
            variant="contained"
            color="primary"
            href={indexUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Create index in Firebase Console
          </Button>
        </>
      );
    } else if (indexUrl && !adminMember) {
      errorBody = (
        <Typography color="textSecondary" variant="body1">
          The request list could not be loaded because of a server configuration issue. Please try
          again later or contact your administrator.
        </Typography>
      );
    } else {
      errorBody = <Typography color="error">{error}</Typography>;
    }
    return <Box p={2}>{errorBody}</Box>;
  }
  if (!requests.length) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">{emptyMessage}</Typography>
      </Box>
    );
  }
  if (!displayedRequests.length) {
    return (
      <Box p={3}>
        <Box mb={2} maxWidth={400}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Search by ticket number"
            value={ticketSearch}
            onChange={e => setTicketSearch(e.target.value)}
            inputProps={{ 'aria-label': 'Search requests by ticket number' }}
          />
        </Box>
        <Typography color="textSecondary">No requests match that ticket number.</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Box mb={2} maxWidth={400}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label="Search by ticket number"
          value={ticketSearch}
          onChange={e => setTicketSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search requests by ticket number' }}
        />
      </Box>
      <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Request ID</TableCell>
            <TableCell>Request name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell sortDirection={sortBy === 'created' ? sortDir : false}>
              <TableSortLabel
                active={sortBy === 'created'}
                direction={sortBy === 'created' ? sortDir : 'asc'}
                onClick={() => handleSort('created')}
              >
                Created
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === 'last_updated' ? sortDir : false}>
              <TableSortLabel
                active={sortBy === 'last_updated'}
                direction={sortBy === 'last_updated' ? sortDir : 'asc'}
                onClick={() => handleSort('last_updated')}
              >
                Last updated
              </TableSortLabel>
            </TableCell>
            <TableCell>Updates</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedRequests.map((r) => {
            const progressEvents = sortTimelineEvents(r.audit?.last_updated_status ?? []);
            const progressTooltip = (
              <Box component="span" display="block" p={1} maxWidth={360}>
                <Typography variant="subtitle2" gutterBottom>
                  Status: {effectiveRequestStatus(r)}
                  {requestDocIndicatesRejected(r) ? ' (rejected)' : ''}
                  {requestDocIndicatesUserCancelled(r) ? ' (cancelled by user)' : ''}
                </Typography>
                <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
                  Progress
                </Typography>
                {progressEvents.length === 0 ? (
                  <Typography variant="caption" color="textSecondary">
                    No status updates
                  </Typography>
                ) : (
                  progressEvents.map((e, i) => (
                    <Box key={`${e.event}-${i}`} display="block" mb={0.5}>
                      <Typography variant="caption" component="span">
                        {e.event.replace(/_/g, ' ').replace(/\bbackstage\b/gi, 'Iron Phoenix')}
                        {e.timestamp?.trim() && (
                          <> — {formatInstantInUserTimezone(e.timestamp)}</>
                        )}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            );
            return (
              <Tooltip
                key={r.request_id}
                title={progressTooltip}
                placement="top"
                enterDelay={300}
                leaveDelay={0}
              >
                <TableRow
                  hover
                  className={rowStyles.clickableRow}
                  onClick={() =>
                    window.open(
                      `${window.location.origin}/requests/${r.request_id}`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  <TableCell>
                    <RouterLink
                      to={`/requests/${r.request_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        textDecoration: 'underline',
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      {r.request_id}
                    </RouterLink>
                  </TableCell>
                  <TableCell>{r.request_name}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6 }}>
                      <Chip
                        label={effectiveRequestStatus(r)}
                        size="small"
                        color={effectiveRequestStatus(r) === 'closed' ? 'default' : 'primary'}
                        variant="outlined"
                      />
                      {requestDocIndicatesRejected(r) ? (
                        <Chip
                          label="Rejected"
                          size="small"
                          style={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 100%)`,
                            color: theme.palette.primary.contrastText,
                            fontWeight: 700,
                            borderRadius: 8,
                            border: 'none',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
                          }}
                        />
                      ) : null}
                      {requestDocIndicatesUserCancelled(r) ? (
                        <Chip
                          label="cancelled by user"
                          size="small"
                          style={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 100%)`,
                            color: theme.palette.primary.contrastText,
                            fontWeight: 700,
                            borderRadius: 8,
                            border: 'none',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
                          }}
                        />
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {formatInstantInUserTimezone(r.audit?.created_at)}
                  </TableCell>
                  <TableCell>
                    {formatInstantInUserTimezone(r.audit?.last_updated_at)}
                  </TableCell>
                  <TableCell>
                    <UpdatesMetering events={r.audit?.last_updated_status ?? []} />
                  </TableCell>
                </TableRow>
              </Tooltip>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
}

const LIMIT_OPTIONS = [10, 20, 50, 100];

/** Header sent with requests so backend can use the same email as Settings → Profile. */
const BACKSTAGE_USER_EMAIL_HEADER = 'X-Backstage-User-Email';

/** Poll Firestore-backed list so new tickets appear without changing page size. */
const REQUESTS_POLL_INTERVAL_MS = 10_000;

/** Wait for auth/session to be ready (e.g. after K8s load). Retries getProfileInfo a few times with delay. */
async function waitForIdentity(
  identityApi: { getProfileInfo: () => Promise<{ email?: string }> },
  maxAttempts = 4,
  delayMs = 400,
): Promise<void> {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      await identityApi.getProfileInfo();
      return;
    } catch {
      if (i < maxAttempts - 1) {
        await new Promise(r => setTimeout(r, delayMs));
      }
    }
  }
}

/** Fetch with one retry on 401 to handle session/cookie timing in K8s. */
async function fetchWithRetryOn401(
  fetchApi: { fetch: (url: string, init?: { headers?: Record<string, string> }) => Promise<Response> },
  url: string,
  headers: Record<string, string> | undefined,
  retryDelayMs = 600,
): Promise<Response> {
  let res = await fetchApi.fetch(url, headers ? { headers } : undefined);
  if (res.status === 401 && retryDelayMs > 0) {
    await new Promise(r => setTimeout(r, retryDelayMs));
    res = await fetchApi.fetch(url, headers ? { headers } : undefined);
  }
  return res;
}

export const RequestsPage = () => {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const adminMember = useAdminGroupMember();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'open' | 'closed' | 'admin-all'>('open');
  /** Sub-filter for Closed tab only. */
  const [closedFilter, setClosedFilter] = useState<'all' | 'rejected' | 'cancelled'>('all');
  const [limit, setLimit] = useState(10);
  const [openRequests, setOpenRequests] = useState<RequestDoc[]>([]);
  const [closedRequests, setClosedRequests] = useState<RequestDoc[]>([]);
  const [openLoading, setOpenLoading] = useState(true);
  const [closedLoading, setClosedLoading] = useState(true);
  const [openError, setOpenError] = useState<string | null>(null);
  const [closedError, setClosedError] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    waitForIdentity(identityApi).then(() => {
      if (!cancelled) setAuthReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('tab') === 'logs') {
      navigate({ pathname: '/requests/ticket-logs', search: '' }, { replace: true });
    }
  }, [location.search, navigate]);

  const handleTabChange = (_: unknown, value: 'open' | 'closed' | 'admin-all') => {
    setTab(value);
  };

  const loadOpen = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!authReady) return;
      setOpenError(null);
      if (!opts?.silent) {
        setOpenLoading(true);
      }
      try {
        const baseUrl = config.getString('backend.baseUrl');
        const url = `${baseUrl}/api/requests/requests?status=open&limit=${limit}`;
        const headers: Record<string, string> = {};
        try {
          const profile = await identityApi.getProfileInfo();
          const email = profile.email?.trim();
          if (email && email.includes('@')) headers[BACKSTAGE_USER_EMAIL_HEADER] = email;
        } catch {
          /* use no header if profile unavailable */
        }
        const res = await fetchWithRetryOn401(fetchApi, url, Object.keys(headers).length > 0 ? headers : undefined);
        if (!res.ok) {
          const body = await res.text();
          if (res.status === 401) {
            throw new Error('Sign in required. Please refresh the page or sign in again to view your requests.');
          }
          throw new Error(body || `HTTP ${res.status}`);
        }
        const data = (await res.json()) as { requests: RequestDoc[] };
        setOpenRequests(data.requests ?? []);
      } catch (e) {
        setOpenError(e instanceof Error ? e.message : 'Failed to load open requests');
        setOpenRequests([]);
      } finally {
        if (!opts?.silent) {
          setOpenLoading(false);
        }
      }
    },
    [config, fetchApi, identityApi, limit, authReady],
  );

  const loadClosed = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!authReady) return;
      setClosedError(null);
      if (!opts?.silent) {
        setClosedLoading(true);
      }
      try {
        const baseUrl = config.getString('backend.baseUrl');
        const url = `${baseUrl}/api/requests/requests?status=closed&limit=${limit}`;
        const headers: Record<string, string> = {};
        try {
          const profile = await identityApi.getProfileInfo();
          const email = profile.email?.trim();
          if (email && email.includes('@')) headers[BACKSTAGE_USER_EMAIL_HEADER] = email;
        } catch {
          /* use no header if profile unavailable */
        }
        const res = await fetchWithRetryOn401(fetchApi, url, Object.keys(headers).length > 0 ? headers : undefined);
        if (!res.ok) {
          const body = await res.text();
          if (res.status === 401) {
            throw new Error('Sign in required. Please refresh the page or sign in again to view your requests.');
          }
          throw new Error(body || `HTTP ${res.status}`);
        }
        const data = (await res.json()) as { requests: RequestDoc[] };
        setClosedRequests(data.requests ?? []);
      } catch (e) {
        setClosedError(e instanceof Error ? e.message : 'Failed to load closed requests');
        setClosedRequests([]);
      } finally {
        if (!opts?.silent) {
          setClosedLoading(false);
        }
      }
    },
    [config, fetchApi, identityApi, limit, authReady],
  );

  useEffect(() => {
    loadOpen();
  }, [loadOpen]);

  useEffect(() => {
    loadClosed();
  }, [loadClosed]);

  useEffect(() => {
    if (!authReady) return undefined;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      loadOpen({ silent: true });
      loadClosed({ silent: true });
    };
    const id = setInterval(refresh, REQUESTS_POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadOpen({ silent: true });
        loadClosed({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, [authReady, loadOpen, loadClosed]);

  const closedRequestsFiltered = useMemo(() => {
    if (closedFilter === 'rejected') {
      return closedRequests.filter(r => requestDocIndicatesRejected(r));
    }
    if (closedFilter === 'cancelled') {
      return closedRequests.filter(r => requestDocIndicatesUserCancelled(r));
    }
    return closedRequests;
  }, [closedRequests, closedFilter]);

  let closedEmptyMessage: string;
  if (closedFilter === 'rejected') {
    closedEmptyMessage = 'No closed requests marked as rejected.';
  } else if (closedFilter === 'cancelled') {
    closedEmptyMessage = 'No closed requests cancelled by you.';
  } else {
    closedEmptyMessage = 'No closed requests.';
  }

  return (
    <Page themeId="home">
      <Header title="Requests" subtitle="Open and closed tickets" />
      <Content>
        <Box pt={2}>
          <Box display="flex" flexDirection="row" alignItems="center" flexWrap="wrap">
            <Tabs value={tab} onChange={handleTabChange} indicatorColor="primary">
              <Tab label="Open requests" value="open" />
              <Tab label="Closed" value="closed" />
              {adminMember === true ? (
                <Tab label="Admin" value="admin-all" />
              ) : null}
            </Tabs>
            {tab !== 'admin-all' ? (
              <FormControl size="small" style={{ minWidth: 120, marginLeft: 16 }}>
                <InputLabel id="requests-limit-label">Show</InputLabel>
                <Select
                  labelId="requests-limit-label"
                  value={limit}
                  label="Show"
                  onChange={e => {
                    const v = Number(e.target.value);
                    if (LIMIT_OPTIONS.includes(v)) setLimit(v);
                  }}
                >
                  {LIMIT_OPTIONS.map(n => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {tab === 'closed' ? (
              <FormControl component="fieldset" style={{ marginLeft: 24 }}>
                <RadioGroup
                  row
                  aria-label="Closed requests filter"
                  name="closed-filter"
                  value={closedFilter}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === 'all' || v === 'rejected' || v === 'cancelled') {
                      setClosedFilter(v);
                    }
                  }}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio color="primary" size="small" />}
                    label="All"
                  />
                  <FormControlLabel
                    value="rejected"
                    control={<Radio color="primary" size="small" />}
                    label="Rejected"
                  />
                  <FormControlLabel
                    value="cancelled"
                    control={<Radio color="primary" size="small" />}
                    label="Cancelled"
                  />
                </RadioGroup>
              </FormControl>
            ) : null}
          </Box>
          <Box pt={2}>
            {tab === 'open' && (
              <RequestsTable
                requests={openRequests}
                loading={openLoading}
                error={openError}
                emptyMessage="No open or in-progress requests."
                adminMember={adminMember}
              />
            )}
            {tab === 'closed' && (
              <RequestsTable
                requests={closedRequestsFiltered}
                loading={closedLoading}
                error={closedError}
                emptyMessage={closedEmptyMessage}
                adminMember={adminMember}
              />
            )}
            {tab === 'admin-all' && adminMember === true ? <AdminAllTicketsPanel /> : null}
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
